import { toast } from "react-hot-toast";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField } from "@mui/material";
import {
  ArrowPathRoundedSquareIcon as SyncIcon,
  ArrowRightIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import ActivitiesTable from "../../../components/dashboard/activities/activitiesTable";
import AbsenceTypesStacklist from "../../../components/dashboard/absences/types/stacklist";
import ModernEmployeeSelector from "../../../components/utils/Selector/ModernEmployeeSelector";
import { EmployeeSelector } from "../../../components/utils/moduleSelectors/employeeSelector";
import {
  fetchHourSettings,
  queueFetchHoursJob,
  updateHourSettings,
} from "../../../api/hour";

const HourSettingsCard = ({ id, title, description, children }) => (
  <section
    id={id}
    className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
  >
    <div className="mb-4">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
    </div>
    <div className="space-y-6">{children}</div>
  </section>
);

const NumberField = ({ label, value, min, max, disabled, onChange, onBlur, placeholder }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-slate-700">{label}</label>
    <input
      type="number"
      min={min}
      max={max}
      value={value ?? ""}
      placeholder={placeholder}
      disabled={disabled}
      onChange={onChange}
      onBlur={onBlur}
      className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50"
    />
  </div>
);

const ToggleField = ({ label, description, checked, disabled, onChange }) => (
  <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/50 p-3">
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400 disabled:cursor-not-allowed"
    />
    <div>
      <div className="text-sm font-medium text-slate-900">{label}</div>
      {description ? <div className="text-sm text-slate-600">{description}</div> : null}
    </div>
  </label>
);

const HandlerSelector = ({
  title,
  description,
  selected,
  disabled,
  onOpen,
  onClear,
  placeholder,
}) => (
  <div className="col-span-6 sm:col-span-3">
    <label className="mb-2 block text-sm font-medium text-slate-700">{title}</label>
    <p className="mb-3 text-sm text-slate-600">{description}</p>
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={onOpen}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-50"
      >
        {selected?.displayTitle || selected?.name || placeholder}
      </button>
      {selected?.id ? (
        <button
          type="button"
          disabled={disabled}
          onClick={onClear}
          className="absolute right-2 top-2 text-slate-400 hover:text-slate-600 disabled:cursor-not-allowed"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      ) : null}
    </div>
  </div>
);

export const HourSettings = () => {
  const [settings, setSettings] = useState({});
  const [draftSettings, setDraftSettings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectorTarget, setSelectorTarget] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncForm, setSyncForm] = useState({
    from_date: "",
    to_date: "",
    user_id: "",
  });
  const { t } = useTranslation();
  const location = useLocation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canSeeSync = isAdmin && !!currentCompany?.activeTripletexIntegration;

  const getSettings = () => {
    setIsLoading(true);
    fetchHourSettings()
      .then((res) => {
        const data = res?.data?.data || {};
        setSettings(data);
        setDraftSettings(data);
      })
      .catch(() => {
        toast.error(t("Could not load settings"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getSettings();
  }, []);

  useEffect(() => {
    const raw = location.hash?.replace(/^#/, "");
    if (!raw) return;
    const el = document.getElementById(raw);
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash, isLoading]);

  const saveSettings = async (payload, successMessage = t("Settings saved")) => {
    setSavingKey(Object.keys(payload).join(","));
    try {
      const response = await updateHourSettings(payload);
      const hasError = response?.response?.status || response?.error;
      if (hasError) throw response;

      const data = response?.data || response?.data?.data;
      if (data) {
        setSettings(data);
        setDraftSettings(data);
      } else {
        setSettings((prev) => ({ ...prev, ...payload }));
        setDraftSettings((prev) => ({ ...prev, ...payload }));
      }

      toast.success(successMessage);
      return true;
    } catch {
      setDraftSettings((prev) => ({ ...prev, ...settings }));
      toast.error(t("Kunne ikke lagre endring"));
      return false;
    } finally {
      setSavingKey(null);
    }
  };

  const saveHandler = async (key, employee) => {
    const idKey = `${key}_id`;
    const payload = { [idKey]: employee?.id || null };

    setDraftSettings((prev) => ({
      ...prev,
      [key]: employee || null,
      [idKey]: employee?.id || null,
    }));

    const ok = await saveSettings(payload);
    if (ok) {
      setSettings((prev) => ({
        ...prev,
        [key]: employee || null,
        [idKey]: employee?.id || null,
      }));
    }
  };

  const updateDraftValue = (key, value) => {
    setDraftSettings((prev) => ({ ...prev, [key]: value }));
  };

  const saveDraftValue = (key, transform = (value) => value) => {
    const nextValue = transform(draftSettings[key]);
    if (settings[key] === nextValue) {
      setDraftSettings((prev) => ({ ...prev, [key]: nextValue }));
      return;
    }

    setDraftSettings((prev) => ({ ...prev, [key]: nextValue }));
    void saveSettings({ [key]: nextValue });
  };

  const headerActions = useMemo(
    () =>
      [
        canSeeSync
          ? {
              component: null,
              label: t("Sync Tripletex hours"),
              description: "",
              path: null,
              action: () => setSyncDialogOpen(true),
              icon: SyncIcon,
              textColor: "text-teal-700",
              bgColor: "bg-white",
              hidden: false,
              menu: true,
            }
          : null,
      ].filter(Boolean),
    [canSeeSync, t]
  );

  const onSubmitSync = async () => {
    try {
      const payload = {
        from_date: syncForm.from_date || undefined,
        to_date: syncForm.to_date || undefined,
        user_id: syncForm.user_id ? Number(syncForm.user_id) : undefined,
      };
      const response = await queueFetchHoursJob(payload);
      if (response?.success) {
        toast.success(response.message || t("Sync job queued successfully"));
        setSyncDialogOpen(false);
        setSyncForm({ from_date: "", to_date: "", user_id: "" });
      } else {
        toast.error(response?.errors?.join(", ") || t("Something went wrong. Could not start sync."));
      }
    } catch {
      toast.error(t("Something went wrong. Could not start sync."));
    }
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <DashboardHeader
        subtitle={t("hour_settings.page.subtitle")}
        actions={headerActions}
        title={t("settings")}
        meta={[]}
      >
        <div className="mx-auto mt-8 grid max-w-7xl grid-cols-1 gap-6 px-4 pb-12 sm:px-6">
          <div
            id="guide-hours-intro"
            className="scroll-mt-28 rounded-[2rem] border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                  <BookOpenIcon className="h-4 w-4" />
                  {t("company_guide.badge")}
                </div>
                <h2 className="text-lg font-semibold text-slate-950">{t("hour_settings.guide.title")}</h2>
                <p className="mt-1 max-w-3xl text-sm text-slate-600">{t("hour_settings.guide.intro")}</p>
                <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-slate-600">
                  <li>{t("hour_settings.guide.step.project_activity")}</li>
                  <li>{t("hour_settings.guide.step.absence_types")}</li>
                  <li>{t("hour_settings.guide.step.handlers")}</li>
                  <li>{t("hour_settings.guide.step.payroll_lock")}</li>
                  <li>{t("hour_settings.guide.step.time_documents")}</li>
                  <li>{t("hour_settings.guide.step.work_schedule")}</li>
                </ul>
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
                <Link
                  to="/dashboard/guide/hours"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  {t("hour_settings.guide.open_user_guide")}
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <Link
                  to="/dashboard/"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:border-slate-300 hover:bg-white"
                >
                  {t("hour_settings.guide.back_dashboard")}
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          <HourSettingsCard
            id="guide-hours-project-activities"
            title={t("Activities - Project (General)")}
            description={t("Generelle prosjektlinjer som brukes i timeregistrering.")}
          >
            <ActivitiesTable
              name="project_activities"
              number={true}
              invoiceable={true}
              inactive={true}
              drag={false}
              title={false}
              module="settings"
              values={{ project_activities: draftSettings?.project_activities || [] }}
              setFieldValue={(field, value) => {
                setDraftSettings((prev) => ({ ...prev, [field]: value }));
              }}
              isLoading={isLoading}
            />
          </HourSettingsCard>

          <HourSettingsCard
            id="guide-hours-internal-activities"
            title={t("Activities - Internal (General)")}
            description={t("Interne aktiviteter som kan brukes ved timeregistrering.")}
          >
            <ActivitiesTable
              name="internal_activities"
              number={true}
              invoiceable={true}
              inactive={true}
              drag={false}
              title={false}
              module="settings"
              values={{ internal_activities: draftSettings?.internal_activities || [] }}
              setFieldValue={(field, value) => {
                setDraftSettings((prev) => ({ ...prev, [field]: value }));
              }}
              isLoading={isLoading}
            />
          </HourSettingsCard>

          <HourSettingsCard
            id="guide-hours-absence-types"
            title="Fraværstyper"
            description={t("Vedlikehold fraværstyper som brukes i fraværsregistrering.")}
          >
            <AbsenceTypesStacklist />
          </HourSettingsCard>

          <HourSettingsCard
            id="guide-hours-handlers"
            title={t("Håndtering av timer og fravær")}
            description={t("Velg hvem som skal håndtere timegodkjenning og egenmeldinger.")}
          >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <HandlerSelector
                title={t("Bruker som håndterer egenmelding")}
                description={t("Velg en administrator som skal håndtere egenmeldinger.")}
                selected={draftSettings?.absence_handler}
                disabled={!isAdmin}
                onOpen={() => setSelectorTarget("absence_handler")}
                onClear={() => void saveHandler("absence_handler", null)}
                placeholder={t("Velg administrator")}
              />

              <HandlerSelector
                title={t("Bruker som håndterer timer")}
                description={t("Velg en administrator som skal behandle timer og timelister.")}
                selected={draftSettings?.hours_handler}
                disabled={!isAdmin}
                onOpen={() => setSelectorTarget("hours_handler")}
                onClear={() => void saveHandler("hours_handler", null)}
                placeholder={t("Velg administrator")}
              />
            </div>
          </HourSettingsCard>

          <HourSettingsCard
            id="guide-hours-payroll-lock"
            title={t("Låsing av lønnsperiode")}
            description={t("Bestem når en kalendermåned låses for timeregistrering.")}
          >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <ToggleField
                label={t("Lås måned ved periodeslutt")}
                description={t("Når dette er aktivt, blir kalendermåneden låst etter valgt klokkeslett siste dag i måneden.")}
                checked={draftSettings?.payroll_calendar_month_lock_enabled}
                disabled={!isAdmin}
                onChange={(checked) => {
                  updateDraftValue("payroll_calendar_month_lock_enabled", checked);
                  void saveSettings({ payroll_calendar_month_lock_enabled: checked });
                }}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberField
                  label={t("Låsetidspunkt (0-23)")}
                  value={draftSettings?.payroll_period_lock_hour}
                  min={0}
                  max={23}
                  disabled={!isAdmin}
                  onChange={(e) => updateDraftValue("payroll_period_lock_hour", e.target.value)}
                  onBlur={() =>
                    saveDraftValue("payroll_period_lock_hour", (value) => {
                      const num = Number.parseInt(value, 10);
                      if (Number.isNaN(num)) return 18;
                      return Math.min(23, Math.max(0, num));
                    })
                  }
                />

                <NumberField
                  label={t("Utbetalingsdato")}
                  value={draftSettings?.payroll_payout_day_of_month}
                  min={1}
                  max={31}
                  disabled={!isAdmin}
                  placeholder="1-31"
                  onChange={(e) => updateDraftValue("payroll_payout_day_of_month", e.target.value)}
                  onBlur={() =>
                    saveDraftValue("payroll_payout_day_of_month", (value) => {
                      if (value === "" || value == null) return null;
                      const num = Number.parseInt(value, 10);
                      if (Number.isNaN(num)) return null;
                      return Math.min(31, Math.max(1, num));
                    })
                  }
                />
              </div>
            </div>
          </HourSettingsCard>

          <HourSettingsCard
            id="guide-hours-time-documents"
            title={t("hour_settings.time_documents.section_title")}
            description={t("hour_settings.time_documents.section_description")}
          >
            <ToggleField
              label={t("hour_settings.time_documents.enabled")}
              checked={!!draftSettings?.hours_time_entry_documents_enabled}
              disabled={!isAdmin}
              onChange={(checked) => {
                updateDraftValue("hours_time_entry_documents_enabled", checked);
                void saveSettings({ hours_time_entry_documents_enabled: checked });
              }}
            />
          </HourSettingsCard>

          <HourSettingsCard
            id="guide-hours-reminders"
            title={t("Påminnelse om timer")}
            description={t("Send e-postpåminnelser om manglende timeregistrering. Selve utsendingen må være satt opp i backend scheduler.")}
          >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <ToggleField
                label={t("Aktiver daglig påminnelse")}
                description={t("Brukes sammen med evalueringstidspunkt og sendetidspunkt under.")}
                checked={draftSettings?.hours_daily_registration_reminder_enabled}
                disabled={!isAdmin}
                onChange={(checked) => {
                  updateDraftValue("hours_daily_registration_reminder_enabled", checked);
                  void saveSettings({ hours_daily_registration_reminder_enabled: checked });
                }}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <NumberField
                  label={t("Vurder timer kl.")}
                  value={draftSettings?.hours_registration_evaluation_hour}
                  min={0}
                  max={23}
                  disabled={!isAdmin}
                  onChange={(e) => updateDraftValue("hours_registration_evaluation_hour", e.target.value)}
                  onBlur={() =>
                    saveDraftValue("hours_registration_evaluation_hour", (value) => {
                      const num = Number.parseInt(value, 10);
                      if (Number.isNaN(num)) return 17;
                      return Math.min(23, Math.max(0, num));
                    })
                  }
                />

                <NumberField
                  label={t("Send påminnelse kl.")}
                  value={draftSettings?.hours_daily_reminder_send_hour}
                  min={0}
                  max={23}
                  disabled={!isAdmin}
                  onChange={(e) => updateDraftValue("hours_daily_reminder_send_hour", e.target.value)}
                  onBlur={() =>
                    saveDraftValue("hours_daily_reminder_send_hour", (value) => {
                      const num = Number.parseInt(value, 10);
                      if (Number.isNaN(num)) return 18;
                      return Math.min(23, Math.max(0, num));
                    })
                  }
                />
              </div>
            </div>
          </HourSettingsCard>

          {savingKey ? (
            <div className="text-sm text-slate-500">{t("Lagrer endringer...")}</div>
          ) : null}
        </div>
      </DashboardHeader>

      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("Sync Tripletex hours")}</DialogTitle>
        <DialogContent>
          <div className="grid grid-cols-1 gap-4 pt-2">
            <TextField
              label={t("From date")}
              type="date"
              name="from_date"
              value={syncForm.from_date}
              onChange={(e) => setSyncForm((prev) => ({ ...prev, from_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t("To date")}
              type="date"
              name="to_date"
              value={syncForm.to_date}
              onChange={(e) => setSyncForm((prev) => ({ ...prev, to_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <div>
              <div className="mb-2 text-sm text-gray-600">{t("User (optional)")}</div>
              <EmployeeSelector
                name="user_id"
                noLabel={true}
                onChange={(e) =>
                  setSyncForm((prev) => ({ ...prev, user_id: e?.target?.value }))
                }
                showField={true}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>{t("Cancel")}</Button>
          <Button variant="contained" onClick={onSubmitSync}>
            {t("Start sync")}
          </Button>
        </DialogActions>
      </Dialog>

      {selectorTarget ? (
        <div className="fixed inset-0 z-[9998] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setSelectorTarget(null)}
            />
            <div className="relative z-[9999] max-h-[90vh] w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
              <ModernEmployeeSelector
                open={true}
                selectedEmployees={
                  selectorTarget === "absence_handler"
                    ? draftSettings?.absence_handler
                      ? [draftSettings.absence_handler]
                      : []
                    : draftSettings?.hours_handler
                    ? [draftSettings.hours_handler]
                    : []
                }
                onChange={async (employees) => {
                  const employee = employees?.[0] || null;
                  await saveHandler(selectorTarget, employee);
                  setSelectorTarget(null);
                }}
                onClose={() => setSelectorTarget(null)}
                multiple={false}
                filters={[{ name: "role", value: "admin" }]}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
